import { type ComputedRef, computed } from 'vue';
import { type RouteLocationRaw, useRoute } from 'vue-router';
import { FeatureName } from '@factoryfixinc/ats-interfaces';

import MeService from '@/core/shared/me/me.service';
import { DrawerService } from '@/core/shared/drawer/drawer.service';
import { DialogService } from '@/core/shared/dialog/dialog.service';
import { EmployerFeatureGatingService } from '@/core/shared/employer-feature-gating/employer-feature-gating.service';
import { UpgradeToGoldVariant } from '@/core/shared/dialog/dialog.types';

import BriefCaseSvg from '@/assets/svg/conversations/briefcase-24.svg';
import BriefCaseFilledSvg from '@/assets/svg/conversations/briefcase-filled-24.svg';
import FileSearchOutlineSvg from '@/assets/svg/candidates/file-search-outline.svg';
import FileSearchFilledSvg from '@/assets/svg/candidates/file-search-filled.svg';
import UserSearchSvg from '@/assets/svg/sourcing/user-search-24.svg';
import UserSearchFilledSvg from '@/assets/svg/sourcing/user-search-filled-24.svg';
import PieChartOutlineSvg from '@/assets/svg/performance-dashboard/pie-chart-outline.svg';
import PieChartFilledSvg from '@/assets/svg/performance-dashboard/pie-chart-filled.svg';
import LaborMarketOutlineSvg from '@/assets/svg/labor-market/labor-market-outline.svg';
import ExploreAITeamSvg from '@/assets/svg/explore-ai-team-24.svg';
import ExploreAITeamOutlineSvg from '@/assets/svg/explore-ai-team-outline-24.svg';

/**
 * A single primary navigation entry. Every item carries a label + icon pair
 * (the desktop rail shows the icons; the mobile drawer shows the labels) and is
 * either a route link (`to`) or an action (`onClick`).
 */
export interface MainNavItem {
  key: string;
  label: string;
  /** Default/outline icon (NavbarIcon `:src`). */
  icon: string;
  /** Selected/filled icon (NavbarIcon `:selected-src`). */
  iconSelected: string;
  /** Present for navigation links. */
  to?: RouteLocationRaw;
  /** Present for action items (e.g. opening a modal). */
  onClick?: () => void;
  /** Resolved visibility/gating for the current employer. */
  visible: boolean;
}

/**
 * Single source of truth for the primary navigation items shared by the desktop
 * rail (`DesktopDrawer`) and the mobile drawer (`MobileDrawer`). Returning a
 * composable rather than a constant because three items depend on reactive
 * route/service state (the dynamic Manage Jobs target, the Performance Dashboard
 * gate, and the Labor Market access check).
 */
export function useMainNavItems(): { items: ComputedRef<MainNavItem[]> } {
  const route = useRoute();
  const meService = new MeService();
  const drawerService = new DrawerService();
  const dialogService = new DialogService();
  const featureGatingService = new EmployerFeatureGatingService();

  const hasPerformanceDashboard = computed(() => meService.isEmployerAdmin);

  // Manage Jobs returns to the last-visited conversation tab when we're inside a
  // project context, otherwise the jobs list.
  const conversationsTarget = computed<RouteLocationRaw>(() => {
    // Only a jobs page's query is a jobs filter; Talent Search's `location` would be read as one.
    const query = route.path.startsWith('/jobs') ? route.query : {};
    if (drawerService.visitedConversationsTabRouteName && route.params.projectId) {
      return {
        name: drawerService.visitedConversationsTabRouteName,
        query,
        params: route.params,
      };
    }
    return { name: 'jobs-list', query, params: route.params };
  });

  // Labor Market Reports: gated behind the feature, falling back to the upgrade
  // modal. Also closes the mobile drawer (a no-op on desktop, where the drawer is
  // a permanent rail).
  function openLaborMarketReports(): void {
    if (featureGatingService.hasAccess(FeatureName.LABOR_MARKET_REPORTS)) {
      dialogService.isLaborMarketReportsModalOpen = true;
    } else {
      dialogService.openUpgradeToGoldModal(UpgradeToGoldVariant.LaborMarket);
    }
    drawerService.isMobileDrawerOpen = false;
  }

  const items = computed<MainNavItem[]>(() => [
    {
      key: 'jobs',
      label: 'Manage Jobs',
      icon: BriefCaseSvg,
      iconSelected: BriefCaseFilledSvg,
      to: conversationsTarget.value,
      visible: true,
    },
    {
      key: 'candidates',
      label: 'Candidate Archive',
      icon: FileSearchOutlineSvg,
      iconSelected: FileSearchFilledSvg,
      to: '/candidates',
      visible: true,
    },
    {
      key: 'search',
      label: 'FactoryFix Talent Search',
      icon: UserSearchSvg,
      iconSelected: UserSearchFilledSvg,
      to: '/search',
      visible: true,
    },
    {
      key: 'performance-dashboard',
      label: 'Performance Dashboard',
      icon: PieChartOutlineSvg,
      iconSelected: PieChartFilledSvg,
      to: '/performance-dashboard',
      visible: hasPerformanceDashboard.value,
    },
    {
      key: 'labor-market',
      label: 'Labor Market Reports',
      icon: LaborMarketOutlineSvg,
      iconSelected: LaborMarketOutlineSvg,
      onClick: openLaborMarketReports,
      visible: true,
    },
    {
      key: 'ai-recruiting-team',
      label: 'My AI Recruiting Team',
      icon: ExploreAITeamOutlineSvg,
      iconSelected: ExploreAITeamSvg,
      to: '/ai-recruiting-team',
      visible: true,
    },
  ]);

  return { items };
}
